import { NodeEnv } from '@mondaygpt/constants'
import {
  CustomerInfo,
  ErrorCode,
  LogLevel,
  Offering,
  Package,
  Purchases,
  PurchasesError,
} from '@revenuecat/purchases-js'

export class RevenueCatService {
  private static instance: RevenueCatService

  private constructor() {}

  static getInstance(): RevenueCatService {
    if (!RevenueCatService.instance) {
      RevenueCatService.instance = new RevenueCatService()
    }
    return RevenueCatService.instance
  }

  async configure(apiKey: string, userId: string): Promise<void> {
    try {
      // Set log level for debugging (optional)
      if (process.env.NODE_ENV === NodeEnv.Development) {
        Purchases.setLogLevel(LogLevel.Debug)
      }

      if (Purchases.isConfigured()) {
        const currentUserId = Purchases.getSharedInstance().getAppUserId()
        if (currentUserId === userId) {
          console.log('RevenueCat already configured for this user')
          return
        }

        // Different user - switch
        await Purchases.getSharedInstance().changeUser(userId)
        console.log('RevenueCat user changed successfully')
        return
      }

      // First time configuration
      await Purchases.configure({
        apiKey,
        appUserId: userId,
      })

      console.log('RevenueCat configured successfully')
    } catch (error) {
      console.error('Failed to configure RevenueCat:', error)
      throw error
    }
  }

  async getOfferings(): Promise<Offering[]> {
    try {
      const offerings = await Purchases.getSharedInstance().getOfferings()
      return Object.values(offerings.all)
    } catch (error) {
      console.error('Failed to get offerings:', error)
      throw error
    }
  }

  async getCurrentOffering(): Promise<Offering | null> {
    try {
      const offerings = await Purchases.getSharedInstance().getOfferings()
      return offerings.current
    } catch (error) {
      console.error('Failed to get current offering:', error)
      throw error
    }
  }

  /** Fetches a specific offering (promo-code offering swaps). Null when the id doesn't exist. */
  async getOfferingById(offeringId: string): Promise<Offering | null> {
    try {
      const offerings = await Purchases.getSharedInstance().getOfferings({
        offeringIdentifier: offeringId,
      })
      return offerings.all[offeringId] ?? null
    } catch (error) {
      console.error('Failed to get offering by id:', error)
      throw error
    }
  }

  async purchase(rcPackage: Package, customerEmail: string): Promise<CustomerInfo | null> {
    try {
      const purchaseResult = await Purchases.getSharedInstance().purchase({
        rcPackage,
        customerEmail,
      })
      return purchaseResult.customerInfo
    } catch (error) {
      if (error instanceof PurchasesError) {
        if (error.errorCode === ErrorCode.UserCancelledError) {
          return null
        }
        console.error('RevenueCat purchase error:', error.errorCode, error.message)
        if (error.errorCode === ErrorCode.ProductAlreadyPurchasedError) {
          // One RC customer cannot buy the same Web Billing product twice, so
          // buying the same plan for a second business hard-fails until
          // per-business app_user_ids ship. Surface the workaround instead of
          // the raw SDK text.
          throw new Error(
            'You already have an active subscription for this plan. To run a second business on the same plan, message us from the in-app chat and we will set it up for you.'
          )
        }
        throw new Error(`Purchase failed: ${error.message}`)
      }
      throw error
    }
  }

  async getCustomerInfo(): Promise<CustomerInfo> {
    try {
      const customerInfo = await Purchases.getSharedInstance().getCustomerInfo()
      return customerInfo
    } catch (error) {
      console.error('Failed to get customer info:', error)
      throw error
    }
  }

  async getManagementURL(): Promise<string | null> {
    try {
      const customerInfo = await this.getCustomerInfo()
      return customerInfo.managementURL || null
    } catch (error) {
      console.error('Failed to get management URL:', error)
      return null
    }
  }

  async openManageSubscriptions(): Promise<void> {
    const managementURL = await this.getManagementURL()
    if (managementURL) {
      window.open(managementURL, '_blank')
    } else {
      console.warn('No management URL available')
    }
  }
}
